// FeatureTabDiagrams.tsx
// Drop-in replacements for the weak clip-art images in the home-page feature tabs.
// Pure inline-styled React + SVG — no extra deps. Uses the Primary blue palette.
//
// Requires two keyframes in globals.css:
//   @keyframes ftPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.35;transform:scale(.6)} }
//   @keyframes ftDash  { to { stroke-dashoffset:-16 } }
//
// Each diagram is authored at a fixed 460x300 design size, then uniformly
// scaled to fit its slot via ScaledDiagram (ResizeObserver-driven). Scaling
// everything together keeps every chip/node/line at its exact relative
// spacing at any container width, instead of fixed-px chips overflowing or
// overlapping once the slot shrinks below the design width (e.g. on mobile).

'use client';

import { CSSProperties, ReactElement, ReactNode, useLayoutEffect, useRef, useState } from 'react';

const MONO = "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

/**
 * Diagram palette, read off the Figma control-plane graphic (node 10323:23826)
 * and its bound variables.
 *
 * These are deliberately NOT `--color-primary-default` (#1C75F6). That is the
 * interface's action blue — correct for the tab chrome above, which uses it in
 * Figma too — but the design system draws the graphics themselves on its
 * Ocean/Signal Blue ramp. Mixing the two is what the "wrong hex" report caught.
 */
const C = {
  /** Ocean Blue/500 — deepest fill, badges, solid end of gradients. */
  ocean: '#0000A3',
  /** Ocean Blue/400 — the workhorse accent: dots, ticks, live strokes. */
  accent: '#3333B5',
  /** Signal Blue/500 — the light end of a filled node's gradient. */
  signal: '#4D4DBF',
  /** Signal Blue/24% — connector curves and accent hairlines. */
  line: 'rgba(77,77,191,.24)',
  /** Signal Blue/8% — wash behind a small icon. */
  wash: 'rgba(77,77,191,.08)',
  /** Aqua Blue/300 — caption on a filled node, and the code block's keywords. */
  aqua: '#B8E8FA',
  /** Secondary/Red/500 — the one non-blue accent in the reference. */
  red: '#E94E85',
  /** BG/BG 1 and Black/50. */
  surface: '#F2F2F3',
  hairline: '#E6E6E6',
  /** Greys keep their own ramp — the reference has no token for them. */
  muted: '#6B7280',
  faint: '#9AA3AF',
} as const;

/** Shadows retinted to the ramp, replacing the old #1C75F6-keyed ones. */
const SHADOW = {
  node: '0 14px 28px -8px rgba(0,0,163,.45)',
};

const DESIGN_WIDTH = 460;
const DESIGN_HEIGHT = 300;

const scaleOuter: CSSProperties = {
  position: 'relative',
  width: '100%',
  maxWidth: DESIGN_WIDTH,
  aspectRatio: `${DESIGN_WIDTH} / ${DESIGN_HEIGHT}`,
};

/**
 * Uniformly scales its fixed 460x300 design to fill the available width.
 *
 * The scale is measured from the wrapper width. A tab that starts hidden
 * (`display:none`) has width 0, and a later viewport resize while hidden never
 * reaches it — so we must re-measure the moment it becomes visible again, not
 * just on mount/resize. IntersectionObserver fires exactly on that visibility
 * flip; combined with a ResizeObserver (live resizes) and a rAF settle it keeps
 * the scale correct across every tab-switch + resize ordering.
 */
const ScaledDiagram = ({ children }: { children: ReactNode }): ReactElement => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const measure = () => {
      if (el.clientWidth > 0) setScale(el.clientWidth / DESIGN_WIDTH);
    };
    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const io = new IntersectionObserver(measure);
    io.observe(el);
    window.addEventListener('resize', measure);

    return () => {
      ro.disconnect();
      io.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  return (
    <div ref={wrapRef} style={scaleOuter}>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: DESIGN_WIDTH,
          height: DESIGN_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        {children}
      </div>
    </div>
  );
};

const svgLayer: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  overflow: 'visible',
  zIndex: 0,
};

const nodePrimary = (extra: CSSProperties): CSSProperties => ({
  position: 'absolute',
  transform: 'translate(-50%,-50%)',
  zIndex: 2,
  background: `linear-gradient(155deg,${C.signal},${C.ocean})`,
  boxShadow: SHADOW.node,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  ...extra,
});

const chip: CSSProperties = {
  position: 'absolute',
  transform: 'translate(-50%,-50%)',
  zIndex: 1,
  background: '#fff',
  border: `1px solid ${C.hairline}`,
  borderRadius: 10,
  boxShadow: '0 1px 3px rgba(20,21,26,.07)',
  padding: '8px 11px',
};

const chipTitle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600 };
const chipDot: CSSProperties = { width: 7, height: 7, borderRadius: '50%', background: C.accent };
const chipMono: CSSProperties = { fontFamily: MONO, fontSize: 10, color: C.muted, marginTop: 2 };

/* ───────────────────────── 1. Policy Engine ───────────────────────── */
export const PolicyEngineDiagram = (): ReactElement => (
  <ScaledDiagram>
    <svg viewBox="0 0 460 300" preserveAspectRatio="none" style={svgLayer}>
      <path d="M126,55 C160,55 165,145 190,145" fill="none" stroke={C.line} strokeWidth={1.5} />
      <path d="M126,115 C165,115 170,145 190,145" fill="none" stroke={C.line} strokeWidth={1.5} />
      <path d="M126,175 C165,175 170,145 190,145" fill="none" stroke={C.line} strokeWidth={1.5} />
      <path d="M126,235 C160,235 165,145 190,145" fill="none" stroke={C.line} strokeWidth={1.5} />
      <path d="M300,122 C325,122 322,100 342,100" fill="none" stroke={C.accent} strokeWidth={2} strokeDasharray="4 4" style={{ animation: 'ftDash 1s linear infinite' }} />
      <path d="M300,168 C325,168 322,196 342,196" fill="none" stroke={C.hairline} strokeWidth={1.5} />
    </svg>

    <div style={{ ...chip, left: '15.7%', top: '18.3%', width: 116 }}>
      <div style={chipTitle}><span style={chipDot} />Identity</div>
      <div style={chipMono}>role: admin</div>
    </div>
    <div style={{ ...chip, left: '15.7%', top: '38.3%', width: 116 }}>
      <div style={chipTitle}><span style={chipDot} />Device</div>
      <div style={chipMono}>trust: managed</div>
    </div>
    <div style={{ ...chip, left: '15.7%', top: '58.3%', width: 116 }}>
      <div style={chipTitle}><span style={chipDot} />Risk</div>
      <div style={chipMono}>score: low</div>
    </div>
    <div style={{ ...chip, left: '15.7%', top: '78.3%', width: 116 }}>
      <div style={chipTitle}><span style={chipDot} />Context</div>
      <div style={chipMono}>geo: US · 09:14</div>
    </div>

    <div style={nodePrimary({ left: '53.3%', top: '48.3%', width: 120, height: 96, borderRadius: 16, gap: 7 })}>
      <span style={{ position: 'absolute', top: 9, right: 9, width: 8, height: 8, borderRadius: '50%', background: '#8FE3A0', boxShadow: '0 0 0 3px rgba(143,227,160,.25)', animation: 'ftPulse 1.8s ease-in-out infinite' }} />
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M12 2L4 5v6c0 4.5 3.1 8.6 8 10 4.9-1.4 8-5.5 8-10V5l-8-3z" stroke="#fff" strokeWidth={1.6} fill="rgba(255,255,255,.12)" /><path d="M8.5 12l2.4 2.4L15.8 9.5" stroke="#fff" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" /></svg>
      <span style={{ fontSize: 12, fontWeight: 600 }}>Policy Engine</span>
      <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', color: C.aqua }}>ABAC · evaluate</span>
    </div>

    <div style={{ position: 'absolute', left: '87.3%', top: '33.3%', transform: 'translate(-50%,-50%)', zIndex: 1, width: 120, background: '#fff', border: `1px solid ${C.line}`, borderRadius: 12, boxShadow: '0 6px 14px -6px rgba(51,51,181,.3)', padding: '9px 11px', display: 'flex', alignItems: 'center', gap: 9 }}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill={C.accent} /><path d="M7.5 12.3l3 3 6-6.5" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" /></svg>
      <div><div style={{ fontSize: 12, fontWeight: 600 }}>Allow</div><div style={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>read · write</div></div>
    </div>
    <div style={{ position: 'absolute', left: '87.3%', top: '65.3%', transform: 'translate(-50%,-50%)', zIndex: 1, width: 120, background: '#fff', border: `1px solid ${C.hairline}`, borderRadius: 12, boxShadow: '0 4px 10px -6px rgba(20,21,26,.15)', padding: '9px 11px', display: 'flex', alignItems: 'center', gap: 9, opacity: 0.9 }}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill={C.faint} /><path d="M8.5 8.5l7 7M15.5 8.5l-7 7" stroke="#fff" strokeWidth={2} strokeLinecap="round" /></svg>
      <div><div style={{ fontSize: 12, fontWeight: 600, color: C.muted }}>Deny</div><div style={{ fontFamily: MONO, fontSize: 9, color: C.faint }}>blocked</div></div>
    </div>
  </ScaledDiagram>
);

/* ───────────────────────── 2. AI Governance ───────────────────────── */
const gate = (allowed: boolean): CSSProperties => ({
  position: 'absolute',
  transform: 'translate(-50%,-50%)',
  zIndex: 2,
  width: 24,
  height: 24,
  borderRadius: 7,
  background: '#fff',
  border: `1px solid ${allowed ? C.line : C.hairline}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: allowed ? '0 2px 6px -2px rgba(51,51,181,.3)' : '0 2px 6px -2px rgba(20,21,26,.15)',
});

export const AIGovernanceDiagram = (): ReactElement => (
  <ScaledDiagram>
    <svg viewBox="0 0 460 300" preserveAspectRatio="none" style={svgLayer}>
      {/* The boundary, its label and the agent inside it move together. 137
          wide starting at 51 lands the right edge on x=188, where the three
          paths below begin, so they leave the boundary rather than crossing it
          or floating clear of it; 25.96% is that box's centre, which is where
          the agent and the label sit. */}
      <rect x="51" y="86" width="137" height="128" rx="18" fill="none" stroke={C.line} strokeWidth={1.5} strokeDasharray="5 5" />
      <path d="M188,140 C235,140 240,80 320,80" fill="none" stroke={C.accent} strokeWidth={2} strokeDasharray="4 4" style={{ animation: 'ftDash 1s linear infinite' }} />
      <path d="M188,150 C245,150 260,150 320,150" fill="none" stroke={C.hairline} strokeWidth={1.5} strokeDasharray="4 4" />
      <path d="M188,160 C235,160 240,220 320,220" fill="none" stroke={C.accent} strokeWidth={2} strokeDasharray="4 4" style={{ animation: 'ftDash 1s linear infinite' }} />
    </svg>

    <div style={{ position: 'absolute', left: '25.96%', top: '26%', transform: 'translate(-50%,-50%)', zIndex: 1, fontFamily: MONO, fontSize: 9, letterSpacing: '.05em', color: C.accent }}>policy boundary</div>

    <div style={nodePrimary({ left: '25.96%', top: '50%', width: 92, height: 92, borderRadius: 20, gap: 6 })}>
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><rect x="5" y="8" width="14" height="10" rx="3" fill="rgba(255,255,255,.14)" stroke="#fff" strokeWidth={1.5} /><circle cx="9.3" cy="13" r="1.4" fill="#fff" /><circle cx="14.7" cy="13" r="1.4" fill="#fff" /><path d="M12 5.5V8M9 5.5h6" stroke="#fff" strokeWidth={1.5} strokeLinecap="round" /></svg>
      <span style={{ fontSize: 11, fontWeight: 600 }}>AI Agent</span>
    </div>

    <div style={{ ...gate(true), left: '64.6%', top: '26.7%' }}><svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M6 12.5l3.5 3.5L18 7" stroke={C.accent} strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" /></svg></div>
    <div style={{ ...gate(false), left: '64.6%', top: '50%' }}><svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M7 7l10 10M17 7L7 17" stroke={C.faint} strokeWidth={2.4} strokeLinecap="round" /></svg></div>
    <div style={{ ...gate(true), left: '64.6%', top: '73.3%' }}><svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M6 12.5l3.5 3.5L18 7" stroke={C.accent} strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" /></svg></div>

    <div style={{ position: 'absolute', left: '82.6%', top: '26.6%', transform: 'translate(-50%,-50%)', zIndex: 1, width: 118, background: '#fff', border: `1px solid ${C.hairline}`, borderRadius: 10, boxShadow: '0 3px 8px -4px rgba(20,21,26,.12)', padding: '8px 10px' }}>
      <div style={{ fontSize: 12, fontWeight: 600 }}>CRM data</div><div style={{ fontFamily: MONO, fontSize: 9, color: C.accent, marginTop: 2 }}>allowed</div>
    </div>
    <div style={{ position: 'absolute', left: '82.6%', top: '50%', transform: 'translate(-50%,-50%)', zIndex: 1, width: 118, background: C.surface, border: `1px solid ${C.hairline}`, borderRadius: 10, boxShadow: '0 3px 8px -4px rgba(20,21,26,.12)', padding: '8px 10px' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: C.muted }}>Prod database</div><div style={{ fontFamily: MONO, fontSize: 9, color: C.faint, marginTop: 2 }}>blocked</div>
    </div>
    <div style={{ position: 'absolute', left: '82.6%', top: '73.3%', transform: 'translate(-50%,-50%)', zIndex: 1, width: 118, background: '#fff', border: `1px solid ${C.hairline}`, borderRadius: 10, boxShadow: '0 3px 8px -4px rgba(20,21,26,.12)', padding: '8px 10px' }}>
      <div style={{ fontSize: 12, fontWeight: 600 }}>Email API</div><div style={{ fontFamily: MONO, fontSize: 9, color: C.accent, marginTop: 2 }}>allowed</div>
    </div>

    <div style={{ position: 'absolute', left: '50%', bottom: '2%', transform: 'translate(-50%,0)', zIndex: 1, display: 'flex', alignItems: 'center', gap: 8, width: 'max-content', background: C.surface, border: `1px solid ${C.line}`, borderRadius: 20, padding: '5px 12px' }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: C.red, animation: 'ftPulse 1.6s ease-in-out infinite' }} />
      <span style={{ fontFamily: MONO, fontSize: 10, color: C.accent, whiteSpace: 'nowrap' }}>prompt · response · tool_call → logged</span>
    </div>
  </ScaledDiagram>
);

/* ─────────────────── 3. Unified Identity Layer ─────────────────── */
const entity = (extra: CSSProperties): CSSProperties => ({
  position: 'absolute',
  transform: 'translate(-50%,-50%)',
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  background: '#fff',
  border: `1px solid ${C.hairline}`,
  borderRadius: 20,
  padding: '6px 12px 6px 8px',
  boxShadow: '0 3px 8px -4px rgba(20,21,26,.12)',
  ...extra,
});
const entityIcon: CSSProperties = { width: 22, height: 22, borderRadius: '50%', background: C.wash, display: 'flex', alignItems: 'center', justifyContent: 'center' };
const verified = (extra: CSSProperties): CSSProperties => ({
  position: 'absolute',
  transform: 'translate(-50%,-50%)',
  zIndex: 2,
  width: 18,
  height: 18,
  borderRadius: '50%',
  background: '#fff',
  border: `1px solid ${C.line}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 2px 5px -2px rgba(51,51,181,.35)',
  ...extra,
});
const check = <svg width="10" height="10" viewBox="0 0 24 24" fill="none"><path d="M6 12.5l3.5 3.5L18 7" stroke={C.accent} strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" /></svg>;

export const IdentityLayerDiagram = (): ReactElement => (
  <ScaledDiagram>
    <svg viewBox="0 0 460 300" preserveAspectRatio="none" style={svgLayer}>
      <circle cx="230" cy="150" r="120" fill="none" stroke={C.line} strokeWidth={1.5} strokeDasharray="3 6" />
      <path d="M95,72 L230,150" stroke={C.line} strokeWidth={1.5} />
      <path d="M365,72 L230,150" stroke={C.line} strokeWidth={1.5} />
      <path d="M95,228 L230,150" stroke={C.line} strokeWidth={1.5} />
      <path d="M365,228 L230,150" stroke={C.line} strokeWidth={1.5} />
    </svg>

    <div style={nodePrimary({ left: '50%', top: '50%', width: 104, height: 104, borderRadius: '50%', gap: 5 })}>
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M12 2l7 3v6c0 4.4-3 8.3-7 9.5C8 19.3 5 15.4 5 11V5l7-3z" fill="rgba(255,255,255,.12)" stroke="#fff" strokeWidth={1.5} /><circle cx="12" cy="10" r="2.2" stroke="#fff" strokeWidth={1.5} /><path d="M8.5 16c.6-1.9 2-2.8 3.5-2.8s2.9.9 3.5 2.8" stroke="#fff" strokeWidth={1.5} strokeLinecap="round" /></svg>
      <span style={{ fontSize: 11, fontWeight: 600 }}>Identity</span>
      <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.05em', color: C.aqua }}>continuous</span>
    </div>

    <div style={entity({ left: '20.6%', top: '24%' })}><span style={entityIcon}><svg width="13" height="13" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="3.2" stroke={C.accent} strokeWidth={1.7} /><path d="M5.5 19c.8-3 3.3-4.5 6.5-4.5s5.7 1.5 6.5 4.5" stroke={C.accent} strokeWidth={1.7} strokeLinecap="round" /></svg></span><span style={{ fontSize: 12, fontWeight: 600 }}>Users</span></div>
    <div style={entity({ left: '79.4%', top: '24%' })}><span style={entityIcon}><svg width="13" height="13" viewBox="0 0 24 24" fill="none"><rect x="5" y="8" width="14" height="10" rx="3" stroke={C.accent} strokeWidth={1.7} /><circle cx="9.3" cy="13" r="1.3" fill={C.accent} /><circle cx="14.7" cy="13" r="1.3" fill={C.accent} /><path d="M12 5.5V8" stroke={C.accent} strokeWidth={1.7} strokeLinecap="round" /></svg></span><span style={{ fontSize: 12, fontWeight: 600 }}>AI Agents</span></div>
    <div style={entity({ left: '20.6%', top: '76%' })}><span style={entityIcon}><svg width="13" height="13" viewBox="0 0 24 24" fill="none"><rect x="4" y="5" width="16" height="6" rx="2" stroke={C.accent} strokeWidth={1.7} /><rect x="4" y="13" width="16" height="6" rx="2" stroke={C.accent} strokeWidth={1.7} /><circle cx="7.5" cy="8" r="1" fill={C.accent} /><circle cx="7.5" cy="16" r="1" fill={C.accent} /></svg></span><span style={{ fontSize: 12, fontWeight: 600 }}>Services</span></div>
    <div style={entity({ left: '79.4%', top: '76%' })}><span style={entityIcon}><svg width="13" height="13" viewBox="0 0 24 24" fill="none"><rect x="6" y="3" width="12" height="18" rx="2.5" stroke={C.accent} strokeWidth={1.7} /><path d="M10.5 18h3" stroke={C.accent} strokeWidth={1.7} strokeLinecap="round" /></svg></span><span style={{ fontSize: 12, fontWeight: 600 }}>Devices</span></div>

    <div style={verified({ left: '36%', top: '37%' })}>{check}</div>
    <div style={verified({ left: '64%', top: '37%' })}>{check}</div>
    <div style={verified({ left: '36%', top: '63%' })}>{check}</div>
    <div style={verified({ left: '64%', top: '63%' })}>{check}</div>
  </ScaledDiagram>
);

/* ───────────────────────── 4. Secure Access ───────────────────────── */
const actor = (extra: CSSProperties): CSSProperties => ({
  position: 'absolute',
  transform: 'translate(-50%,-50%)',
  zIndex: 1,
  width: 112,
  background: '#fff',
  border: `1px solid ${C.hairline}`,
  borderRadius: 10,
  boxShadow: '0 3px 8px -4px rgba(20,21,26,.12)',
  padding: '7px 10px',
  ...extra,
});
const appCard = (extra: CSSProperties): CSSProperties => ({
  position: 'absolute',
  transform: 'translate(-50%,-50%)',
  zIndex: 1,
  width: 118,
  background: '#fff',
  border: `1px solid ${C.line}`,
  borderRadius: 10,
  boxShadow: '0 6px 14px -6px rgba(51,51,181,.28)',
  padding: '8px 10px',
  ...extra,
});

export const SecureAccessDiagram = (): ReactElement => (
  <ScaledDiagram>
    <svg viewBox="0 0 460 300" preserveAspectRatio="none" style={svgLayer}>
      <path d="M124,66 C150,66 150,150 172,150" fill="none" stroke={C.line} strokeWidth={1.5} />
      <path d="M124,150 L172,150" fill="none" stroke={C.line} strokeWidth={1.5} />
      <path d="M124,234 C150,234 150,150 172,150" fill="none" stroke={C.line} strokeWidth={1.5} />
      <path d="M295,128 C325,128 335,104 368,104" fill="none" stroke={C.accent} strokeWidth={2} strokeDasharray="4 4" style={{ animation: 'ftDash 1s linear infinite' }} />
      <path d="M295,172 C325,172 335,196 368,196" fill="none" stroke={C.accent} strokeWidth={2} strokeDasharray="4 4" style={{ animation: 'ftDash 1s linear infinite' }} />
    </svg>

    <div style={actor({ left: '15.5%', top: '22%' })}><div style={{ fontSize: 12, fontWeight: 600 }}>Employees</div><div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, marginTop: 2 }}>managed</div></div>
    <div style={actor({ left: '15.5%', top: '50%' })}><div style={{ fontSize: 12, fontWeight: 600 }}>Contractors</div><div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, marginTop: 2 }}>BYOD</div></div>
    <div style={actor({ left: '15.5%', top: '78%' })}><div style={{ fontSize: 12, fontWeight: 600 }}>Third parties</div><div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, marginTop: 2 }}>unmanaged</div></div>

    <div style={nodePrimary({ left: '50%', top: '50%', width: 130, height: 104, borderRadius: 16, gap: 6 })}>
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><rect x="5" y="10" width="14" height="10" rx="2.5" fill="rgba(255,255,255,.14)" stroke="#fff" strokeWidth={1.5} /><path d="M8 10V7.5a4 4 0 018 0V10" stroke="#fff" strokeWidth={1.5} /><circle cx="12" cy="15" r="1.6" fill="#fff" /></svg>
      <span style={{ fontSize: 12, fontWeight: 600 }}>ZTNA Gateway</span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: MONO, fontSize: 8, letterSpacing: '.05em', color: C.aqua }}><span style={{ width: 6, height: 6, borderRadius: '50%', background: '#8FE3A0' }} />verify · per-session</span>
    </div>

    <div style={appCard({ left: '80%', top: '34.6%' })}><div style={{ fontSize: 12, fontWeight: 600 }}>Workspace</div><div style={{ fontFamily: MONO, fontSize: 9, color: C.accent, marginTop: 2 }}>DLP on</div></div>
    <div style={appCard({ left: '80%', top: '65.3%' })}><div style={{ fontSize: 12, fontWeight: 600 }}>Data store</div><div style={{ fontFamily: MONO, fontSize: 9, color: C.accent, marginTop: 2 }}>DLP on</div></div>
  </ScaledDiagram>
);

/* ───────────────────────── 5. SDK ───────────────────────── */
const kw: CSSProperties = { color: C.aqua };
const str: CSSProperties = { color: '#9FE0C0' };
const fn: CSSProperties = { color: '#8AD0FF' };
const cmt: CSSProperties = { color: '#5B6B8C' };
const sdkChip: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', border: `1px solid ${C.line}`,
  borderRadius: 8, padding: '6px 11px', fontSize: 12, fontWeight: 600, color: C.ocean, boxShadow: '0 2px 6px -3px rgba(51,51,181,.3)',
};
const sdkDot: CSSProperties = { width: 6, height: 6, borderRadius: '50%', background: C.accent };

export const SdkDiagram = (): ReactElement => (
  <div style={{ width: '100%', maxWidth: 440, display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'stretch' }}>
    <div style={{ borderRadius: 12, overflow: 'hidden', boxShadow: '0 18px 34px -14px rgba(11,27,58,.5)', border: '1px solid #14284D' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#0E2044', padding: '10px 14px' }}>
        <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#FF5F57' }} />
        <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#FEBC2E' }} />
        <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#28C840' }} />
        <span style={{ marginLeft: 8, fontFamily: MONO, fontSize: 11, color: '#8FA6CE' }}>app.ts</span>
      </div>
      <div style={{ background: '#0B1B3A', padding: '16px 18px', fontFamily: MONO, fontSize: 12, lineHeight: 1.9, color: '#C6D4EC', overflowX: 'auto' }}>
        <div><span style={kw}>import</span> {'{ Primary }'} <span style={kw}>from</span> <span style={str}>&apos;@primary/sdk&apos;</span></div>
        <div style={{ height: 6 }} />
        <div><span style={kw}>const</span> p = Primary.<span style={fn}>init</span>({'({ env: '}<span style={str}>&apos;prod&apos;</span>{' })'}</div>
        <div><span style={cmt}>{'// verify identity + enforce policy'}</span></div>
        <div><span style={kw}>await</span> p.identity.<span style={fn}>verify</span>(user)</div>
        <div><span style={kw}>await</span> p.policy.<span style={fn}>enforce</span>(request)</div>
        <div>p.ztna.<span style={fn}>connect</span>(app)<span style={{ display: 'inline-block', width: 7, height: 15, background: C.aqua, verticalAlign: -3, marginLeft: 3, animation: 'ftPulse 1.1s steps(1) infinite' }} /></div>
      </div>
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      <span style={sdkChip}><span style={sdkDot} />Auth</span>
      <span style={sdkChip}><span style={sdkDot} />Authorization</span>
      <span style={sdkChip}><span style={sdkDot} />ZTNA</span>
      <span style={sdkChip}><span style={sdkDot} />Governance</span>
    </div>
    <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.04em', color: C.muted }}>Extensible APIs · REST · gRPC · GraphQL</div>
  </div>
);

/* Map keyed by the tab `label` in headerTabs.ts */
export const tabDiagrams: Record<string, () => ReactElement> = {
  'Policy Engine': PolicyEngineDiagram,
  'AI Governance': AIGovernanceDiagram,
  'Unified Identity Layer': IdentityLayerDiagram,
  'Secure Access': SecureAccessDiagram,
  'SDK': SdkDiagram,
};
